import { memo } from 'react';
import { motion } from 'framer-motion';
import { Heart, RotateCw } from 'lucide-react';
import type { Garment } from '@/types';
import { useGarmentImage } from '@/hooks/useGarmentImage';

interface GarmentCardProps {
  garment: Garment;
  onClick?: (garment: Garment) => void;
  onFavorite?: (id: string) => void;
  viewMode?: 'grid' | 'list';
}

const categoryLabels: Record<string, string> = {
  top: 'Top',
  bottom: 'Bottom',
  dress: 'Dress',
  outerwear: 'Outerwear',
  shoes: 'Shoes',
  accessory: 'Accessory',
  bag: 'Bag',
  hat: 'Hat',
  activewear: 'Activewear',
  swimwear: 'Swimwear',
  suit: 'Suit',
  other: 'Other',
};

const colorHexMap: Record<string, string> = {
  black: '#1A1A1A',
  white: '#FFFFFF',
  gray: '#9CA3AF',
  red: '#EF4444',
  blue: '#3B82F6',
  green: '#22C55E',
  yellow: '#EAB308',
  orange: '#F97316',
  purple: '#8B5CF6',
  pink: '#EC4899',
  brown: '#92400E',
  beige: '#D4B896',
  navy: '#1E3A5F',
  burgundy: '#800020',
  olive: '#6B8E23',
  teal: '#14B8A6',
  multicolor: 'conic-gradient(red, yellow, green, blue, purple, red)',
};

export const GarmentCard = memo(function GarmentCard({
  garment,
  onClick,
  onFavorite,
  viewMode = 'grid',
}: GarmentCardProps) {
  const imageUrl = useGarmentImage(garment.imageUrls?.[0] || garment.thumbnailUrl);

  if (viewMode === 'list') {
    return (
      <motion.div
        className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-card
                   cursor-pointer hover:shadow-card-hover transition-shadow"
        onClick={() => onClick?.(garment)}
        whileTap={{ scale: 0.98 }}
        role="button"
        tabIndex={0}
        aria-label={garment.name}
      >
        <img
          src={imageUrl}
          alt={garment.name}
          className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{garment.name}</p>
          <p className="text-xs text-vestir-gray-dark">
            {categoryLabels[garment.category] ?? garment.category}
            {garment.brand && ` - ${garment.brand}`}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {garment.colors.slice(0, 3).map((color) => (
              <span
                key={color}
                className="w-3 h-3 rounded-full border border-vestir-gray/50"
                style={{
                  background: colorHexMap[color] ?? '#ccc',
                }}
              />
            ))}
            <span className="text-[10px] text-vestir-gray-dark flex items-center gap-0.5">
              <RotateCw size={10} />
              {garment.timesWorn}
            </span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFavorite?.(garment.id);
          }}
          className="btn-icon"
          aria-label={garment.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart
            size={18}
            className={garment.isFavorite ? 'fill-vestir-coral text-vestir-coral' : 'text-vestir-gray-dark'}
          />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="relative bg-white rounded-2xl shadow-card overflow-hidden cursor-pointer
                 hover:shadow-card-hover transition-shadow group"
      onClick={() => onClick?.(garment)}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      role="button"
      tabIndex={0}
      aria-label={garment.name}
    >
      {/* Image */}
      <div className="relative aspect-[3/4] overflow-hidden bg-vestir-gray/30">
        <img
          src={imageUrl}
          alt={garment.name}
          className="w-full h-full object-cover transition-transform duration-300
                     group-hover:scale-105"
          loading="lazy"
        />

        {/* Category badge */}
        <span className="absolute top-2 left-2 badge-purple text-[10px]">
          {categoryLabels[garment.category] ?? garment.category}
        </span>

        {/* 3D badge */}
        {garment.meshUrl && (
          <span className="absolute top-2 left-2 mt-6 px-1.5 py-0.5 bg-[#6C3CE1] text-white rounded-md text-[9px] font-bold">
            3D
          </span>
        )}

        {/* Favorite button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFavorite?.(garment.id);
          }}
          className="absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur-sm
                     rounded-full flex items-center justify-center transition-transform
                     hover:scale-110"
          aria-label={garment.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart
            size={16}
            className={
              garment.isFavorite
                ? 'fill-vestir-coral text-vestir-coral'
                : 'text-vestir-gray-dark'
            }
          />
        </button>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="font-medium text-sm truncate">{garment.name}</p>
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1">
            {garment.colors.slice(0, 4).map((color) => (
              <span
                key={color}
                className="w-3 h-3 rounded-full border border-vestir-gray/50"
                style={{
                  background: colorHexMap[color] ?? '#ccc',
                }}
              />
            ))}
            {garment.colors.length > 4 && (
              <span className="text-[10px] text-vestir-gray-dark">
                +{garment.colors.length - 4}
              </span>
            )}
          </div>

          <span className="text-[10px] text-vestir-gray-dark flex items-center gap-0.5">
            <RotateCw size={10} />
            {garment.timesWorn}x
          </span>
        </div>
      </div>
    </motion.div>
  );
});
